import { AuthService, DatabaseService, timestamp, FIREBASE_FUNCTION_HEADERS } from "@/revamp/firebase/config";
import { logError, logger, request } from "@/revamp/utils";
import { getAuth } from "firebase/auth";

async function login(email, password) {
  try {
    let response = await AuthService.signInWithEmailAndPassword(email, password);
    return response;
  } catch (e) {
    throw new Error(e);
  }
}

async function signup(email, password, fname, lname) {
  try {
    let createAuthUserResponse = await AuthService.createUserWithEmailAndPassword(email, password);
    if (!createAuthUserResponse || !createAuthUserResponse.user) {
      logError("UserFunctions.Signup: Signup Failed");
      throw new Error("Signup failed");
    }
    let updateProfileResponse = await createAuthUserResponse.user.updateProfile({ displayName: `${fname} ${lname}` });

    let createBaseUserResponse = await setUserBaseData(createAuthUserResponse.user.uid, fname, lname, email);

    if (createBaseUserResponse == false) {
      throw new Error("Failed to create user base data");
    }

    return { createAuthUserResponse, updateProfileResponse, createBaseUserResponse };
  } catch (e) {
    logError(`UserFunctions.Signup: ${e.toString()}`);
    return null;
  }
}

async function setUserBaseData(uid, fname, lname, email) {
  try {
    let data = {
      uid: uid,
      balance: 0,
      cashed: 0,
      email: email,
      "etransfer email": email,
      fcmtoken: "",
      fname: fname,
      lastNotif: timestamp(),
      lname: lname,
      pendingBalance: 0,
      totalConfirmed: 0,
      totalUnconfirmed: 0,
      totalUserCount: 0,
      tutorialDone: false,
    };

    let response = await DatabaseService.collection("users").doc(uid).set(data, { merge: true });

    return response;
  } catch (e) {
    logError(`UserFunctions.SetUserBaseData: ${e.toString()}`);
    return false;
  }
}

async function sendVerificationEmail(email) {
  try {
    const auth = getAuth();
    const token = await auth.currentUser?.getIdToken(true);

    let response = await fetch(process.env.VUE_APP_FIREBASE_FUNCTIONS_URL + "/SendEmailVerification", {
      method: "POST",
      headers: {
        ...FIREBASE_FUNCTION_HEADERS,
        "x-user-token": token
      },
      body: JSON.stringify({ email: email }),
    });
    return await response.json();
  } catch (e) {
    logError(`UserFunctions.SendVerificationEmail: ${e.toString()}`);
    return null;
  }
}

async function verifyUser(email) {
  try {
    let response = await fetch(process.env.VUE_APP_FIREBASE_FUNCTIONS_URL + "/VerifyUser", {
      method: "POST",
      headers: FIREBASE_FUNCTION_HEADERS,
      body: JSON.stringify({ id: email }),
    });
    let data = await response.json();
    if (data['status'] == 'success') return true; else return false;
  } catch (e) {
    logError(`UserFunctions.VerifyUser: ${e.toString()}`);
    return false;
  }
}

async function checkVerificationLink(link) {
  return await validateLink(link, "verifyLink");
}

async function checkSignUpLink(link) {
  return await validateLink(link, "signUpLink");
}

async function checkResetPasswordLink(link) {
  return await validateLink(link, "passwordResetLink");
}

async function checkChangeEmailLink(link) {
  return await validateLink(link, "emailUpdateLink");
}

async function validateLink(link, type) {
  if (!link || !type) {
    logError(`UserFunctions.ValidateLink: Missing link or type. link: ${link}, type: ${type}`);
    return null;
  }
  try {
    let response = await fetch(`${process.env.VUE_APP_FIREBASE_FUNCTIONS_URL}/linkValidation`, {
      method: "POST",
      headers: FIREBASE_FUNCTION_HEADERS,
      body: JSON.stringify({
        link: link,
        type: type
      }),
    });

    const responseJSON = await response.json();
    if (!responseJSON) return null;
    const isValid = responseJSON.status === "success";
    const expired = responseJSON.code === 10019;
    return {
      email: responseJSON.data?.email ?? null,
      isValid: Boolean(isValid),
      expired: Boolean(expired),
    };
  } catch (e) {
    logError(`UserFunctions.ValidateLink: Failed to validate link. ${e.toString()}`);
    return null;
  }

}

async function sendSignupEmail(email) {
  return await request(`${process.env.VUE_APP_FIREBASE_FUNCTIONS_URL}/SendSignupEmail`, "POST", { email: email }, "UserFunctions.SendSignupEmail");
}

async function finishSignup(signupLink, email, password, fname, lname, willVerify) {
  if (willVerify == null || willVerify == undefined) willVerify = false;
  return await request(`${process.env.VUE_APP_FIREBASE_FUNCTIONS_URL}/CompleteSignup`, "POST", { signupLink, email, password, fname, lname, willVerify }, "UserFunctions.FinishSignup", false);
}

async function forgotPassword(email) {
  try {
    const normalizedEmail = email?.trim().toLowerCase();
    const response = await request(
      `${process.env.VUE_APP_FIREBASE_FUNCTIONS_URL}/sendPasswordReset`,
      "POST",
      { email: normalizedEmail },
      "UserFunctions.forgotPassword",
      false
    );

    return response;
  } catch (e) {
    throw e instanceof Error ? e : new Error(String(e));
  }
}

async function getUserDetails(uid) {
  try {
    let userRef = DatabaseService.collection("users").doc(uid);
    let userDoc = await userRef.get();
    if (!userDoc.exists) throw new Error("User Not Found")

    let data = userDoc.data();
    if (data == null || undefined) throw new Error("User object empty");

    return data;
  } catch (err) {
    logError(`UserFunctions.getUserDetails: Failed to fetch user, ${uid}. ${err.toString}`);
    return undefined
  }
}

async function createAnonymousDonation({
  code,
  charityID,
  platform = undefined,
  source = undefined,
  codeForm = undefined,
  notifyEmail = undefined,
  emailLanguage = "en",
  pin = undefined
}) {
  try {
    const response = await request(
      `${process.env.VUE_APP_FIREBASE_FUNCTIONS_URL}/createAnonymousDonation`,
      "POST",
      { code, charityID, platform, codeForm, source, notifyEmail, emailLanguage, pin },
      "userFunctions.createAnonymousDonation",
      false
    );
    return await response.data;
  } catch (err) {
    logError(`userFunctions.createAnonymousDonation: Failed. ${JSON.stringify({ code, charityID, platform, codeForm, source, notifyEmail, emailLanguage, pin })}. ${err.toString()}`);
    return { error: true, code: err.response?.code, message: err.response?.message };
  }
}

async function getUser(uid) {
  try {
    let user = await request(`${process.env.VUE_APP_FIREBASE_FUNCTIONS_URL}/user`, "POST", { filters: { uid: { op: "eq", "v": uid } } }, "userFunctions.getUser", true);
    if (user.data.length === 0) return null;

    let userData = user.data[0];
    if (!userData.currency) {
      userData.currency = userData.USClaim ? "usd" : "cad";
    }
    return userData;
  } catch (err) {
    logError(`userFunctions.getUser: Failed. ${uid}. ${err.toString()}`);
    return null;
  }
}

async function donateBalance(uid, charityID) {
  try {
    const response = await request(`${process.env.VUE_APP_FIREBASE_FUNCTIONS_URL}/createDonation`, "POST", { uid, charityID }, "userFunctions.donateBalance", true);
    return response.data;
  } catch (err) {
    logger.error(`userFunctions.requestPayout: Failed to request payout. ${uid}. ${err.toString()}`);
    throw err;
  }
}

async function requestPayout(uid, method) {
  try {
    const response = await request(`${process.env.VUE_APP_FIREBASE_FUNCTIONS_URL}/createPaymentRequest`, "POST", { uid, method }, "userFunctions.requestPayout", true);
    return response.data;
  } catch (err) {
    logger.error(`userFunctions.requestPayout: Failed to request payout. ${uid}. ${err.toString()}`);
    throw err;
  }
}

async function getUserPaymentMethods() {
  try {
    const response = await request(`${process.env.VUE_APP_FIREBASE_FUNCTIONS_URL}/getUserPaymentMethods`, "POST", undefined, "userFunctions.getUserPaymentMethods", true);
    return response.data ?? [];
  } catch (err) {
    logger.error(`userFunctions.getUserPaymentMethods: Failed to get payment methods. ${err.toString()}`);
    return [];
  }
}

async function changePaymentEmail(uid, newEmail) {
  try {
    const response = await request(`${process.env.VUE_APP_FIREBASE_FUNCTIONS_URL}/changeTransferEmail`, "POST", { uid, email: newEmail }, "userFunctions.changePaymentEmail", true);
    return response.data;
  } catch (err) {
    logger.error(`userFunctions.changePaymentEmail: Failed to change payment email. ${uid}. ${newEmail}. ${err.toString()}`);
    throw err;
  }
}

const users_functions = {
  login,
  signup,
  finishSignup,
  sendVerificationEmail,
  checkVerificationLink,
  checkSignUpLink,
  checkResetPasswordLink,
  checkChangeEmailLink,
  sendSignupEmail,
  verifyUser,
  getUserDetails,
  getUser,
  createAnonymousDonation,
  donateBalance,
  requestPayout,
  changePaymentEmail,
  getUserPaymentMethods
};

export default users_functions;
